import { useCallback, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import * as Sentry from '@sentry/react';

import {
  CallNoteEditorPage,
  CallNoteData,
} from '@karehero/llama/pwa/pages/CallNoteEditorPage/CallNoteEditorPage';
import type { PortableTextContent } from '@karehero/llama/components/organisms/RichTextEditor/portableText';

import { useGoBack } from 'hooks/useGoBack';
import { DYNAMIC_ROUTES } from 'router/routes';

import type { CareCircle } from '@karehero/models';
import { DocumentTagID } from '@karehero/models';
import { getAccountCareCircles } from 'store/slices/careCircleSlice';
import { newToast } from 'store/slices/uiSlice';
import {
  useRequestOrganiseDocumentUploadURLMutation,
  useConfirmOrganiseDocumentUploadMutation,
  useGetOrganiseDocumentQuery,
  useUpdateOrganiseDocumentMutation,
} from 'store/api/organiseDocument';

interface CallNoteContent {
  title: string;
  content: PortableTextContent;
  createdAt: string;
}

export const CallNoteEditor = () => {
  const dispatch = useDispatch();
  const goBack = useGoBack();
  const navigate = useNavigate();
  const { careCircleID, documentID } = useParams<{
    careCircleID: string;
    documentID?: string;
  }>();
  const careCircles = useSelector(getAccountCareCircles) as CareCircle[] | null;

  const isEditing = !!documentID;

  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [initialTitle, setInitialTitle] = useState('');
  const [initialContent, setInitialContent] = useState<
    PortableTextContent | undefined
  >(undefined);

  const [requestUploadURL] = useRequestOrganiseDocumentUploadURLMutation();
  const [confirmUpload] = useConfirmOrganiseDocumentUploadMutation();
  const [updateDocument] = useUpdateOrganiseDocumentMutation();

  const { data: existingDocument } = useGetOrganiseDocumentQuery(
    { careCircleId: careCircleID ?? '', documentId: documentID ?? '' },
    { skip: !careCircleID || !documentID },
  );

  useEffect(() => {
    const fetchContent = async () => {
      if (!existingDocument?.downloadUrl) return;

      setIsLoadingContent(true);

      try {
        const response = await fetch(existingDocument.downloadUrl);
        if (!response.ok) {
          throw new Error('Failed to fetch call note content');
        }
        const content: CallNoteContent = await response.json();
        setInitialTitle(content.title);
        setInitialContent(content.content);
      } catch (error) {
        Sentry.captureException(error);
        dispatch(
          newToast({
            title: 'Load failed',
            description: 'Failed to load call note content.',
          }),
        );
      } finally {
        setIsLoadingContent(false);
      }
    };

    if (isEditing) {
      fetchContent();
    }
  }, [existingDocument?.downloadUrl, isEditing, dispatch]);

  const careCircle = careCircles?.find((cc) => cc.id === careCircleID);
  const careCircleName = careCircle?.careRecipientAccount
    ? `${careCircle.careRecipientAccount.firstName} ${careCircle.careRecipientAccount.lastName}`
    : undefined;

  const handleSave = useCallback(
    async (data: CallNoteData) => {
      if (!careCircleID) return;

      setIsSaving(true);

      try {
        const jsonContent = JSON.stringify({
          title: data.title,
          content: data.content,
          createdAt: isEditing
            ? (existingDocument?.createdAt ?? new Date().toISOString())
            : new Date().toISOString(),
        });

        const blob = new Blob([jsonContent], { type: 'application/json' });
        const filename = `call-note-${Date.now()}.json`;

        let uploadUrl: string;
        let documentId: string;

        if (isEditing && documentID) {
          const response = await updateDocument({
            careCircleId: careCircleID,
            documentId: documentID,
            body: {
              title: data.title,
              description: 'Call notes document',
              filename,
              contentType: 'application/json',
              sizeBytes: blob.size,
            },
          }).unwrap();

          uploadUrl = response.uploadUrl;
          documentId = response.documentId;
        } else {
          const response = await requestUploadURL({
            careCircleId: careCircleID,
            body: {
              title: data.title,
              description: 'Call notes document',
              filename,
              contentType: 'application/json',
              sizeBytes: blob.size,
              tags: [DocumentTagID.CallNotes],
            },
          }).unwrap();

          uploadUrl = response.uploadUrl;
          documentId = response.documentId;
        }

        if (!uploadUrl) {
          throw new Error('No upload URL in response');
        }

        const isJSONUploadEndpoint = uploadUrl.includes('/upload/storage/');

        const uploadResponse = await fetch(uploadUrl, {
          method: isJSONUploadEndpoint ? 'POST' : 'PUT',
          body: blob,
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!uploadResponse.ok) {
          const errorText = await uploadResponse.text();
          throw new Error(`Upload to storage failed: ${errorText}`);
        }

        await confirmUpload({
          careCircleId: careCircleID,
          body: { documentId },
        }).unwrap();

        dispatch(
          newToast({
            title: 'Call note saved',
            description: 'The call note has been saved successfully.',
          }),
        );

        if (isEditing && careCircleID) {
          navigate(DYNAMIC_ROUTES.ADMIN_CALL_NOTES(careCircleID), {
            replace: true,
          });
        } else {
          goBack();
        }
      } catch (error) {
        Sentry.captureException(error);
        dispatch(
          newToast({
            title: 'Save failed',
            description:
              error instanceof Error
                ? error.message
                : 'An error occurred while saving the call note.',
          }),
        );
      } finally {
        setIsSaving(false);
      }
    },
    [
      careCircleID,
      documentID,
      isEditing,
      existingDocument?.createdAt,
      requestUploadURL,
      updateDocument,
      confirmUpload,
      goBack,
      dispatch,
    ],
  );

  const isLoadingForEdit = isEditing && (isLoadingContent || !initialContent);

  if (isLoadingForEdit) {
    return (
      <CallNoteEditorPage
        careCircleName={careCircleName}
        isEditing={isEditing}
        isSaving={true}
        onBack={goBack}
        onSave={handleSave}
      />
    );
  }

  const editorKey = isEditing ? `edit-${documentID}` : 'new';

  return (
    <CallNoteEditorPage
      key={editorKey}
      initialTitle={initialTitle}
      initialContent={initialContent}
      careCircleName={careCircleName}
      isEditing={isEditing}
      isSaving={isSaving}
      onBack={goBack}
      onSave={handleSave}
    />
  );
};

export default CallNoteEditor;
